import type {
  CreateProcedureCode,
  ImportResult,
  ProcedureCode,
  ProcedureVisitInput,
  UpdateProcedureCode,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The active branch's procedure catalogue. **Branch-scoped** — each branch owns
 * its own outright (ADR-0008), so the branch comes from the header the guard
 * checks, never from a parameter.
 */
export function listProcedureCodes(
  includeRetired = false,
  signal?: AbortSignal,
): Promise<ProcedureCode[]> {
  const query = includeRetired ? "?includeRetired=true" : "";
  return api<ProcedureCode[]>(`/procedure-codes${query}`, { signal, branchScoped: true });
}

export function createProcedureCode(body: CreateProcedureCode): Promise<ProcedureCode> {
  return api<ProcedureCode>("/procedure-codes", { method: "POST", body, branchScoped: true });
}

/** Edit, retire (`isActive: false`) or bring back. Codes are never deleted. */
export function updateProcedureCode(id: string, body: UpdateProcedureCode): Promise<ProcedureCode> {
  return api<ProcedureCode>(`/procedure-codes/${id}`, {
    method: "PATCH",
    body,
    branchScoped: true,
  });
}

/**
 * Set the visits a procedure takes (PV1) — the whole list at once. Empty makes
 * it a one-visit procedure again; the server makes the row's price their sum.
 */
export function setProcedureVisits(
  id: string,
  visits: ProcedureVisitInput[],
): Promise<ProcedureCode> {
  return api<ProcedureCode>(`/procedure-codes/${id}/visits`, {
    method: "PUT",
    body: { visits },
    branchScoped: true,
  });
}

/**
 * Accept the offered starter list — STORY-115, decision TP10.
 *
 * **Content offered, then owned.** The rows this creates are ordinary catalogue
 * rows inside this clinic, editable from the moment they exist, and nothing
 * reads the library again. Declining is not an action: a clinic that never
 * presses the button starts empty exactly as before.
 */
export function acceptStarterLibrary(): Promise<ProcedureCode[]> {
  return api<ProcedureCode[]>("/procedure-codes/starter-library", {
    method: "POST",
    branchScoped: true,
  });
}

/** Fill an empty catalogue from another location's. The rows become this branch's own. */
export function copyCatalogueFrom(branchId: string): Promise<ProcedureCode[]> {
  return api<ProcedureCode[]>("/procedure-codes/copy-from", {
    method: "POST",
    body: { branchId },
    branchScoped: true,
  });
}

/**
 * Import a price list. **The file's text goes to the server unparsed** — one
 * parser, and the per-row reasons come back from the same place that decided
 * them.
 */
export function importCatalogue(csv: string): Promise<ImportResult> {
  return api<ImportResult>("/procedure-codes/import", {
    method: "POST",
    body: { csv },
    branchScoped: true,
  });
}
